<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { getPeripheral, getProvenance } from "../api";
import EntityHeader from "../components/EntityHeader.vue";
import ProvenanceList from "../components/ProvenanceList.vue";
import type { Peripheral, ProvenanceItem } from "../types";
const route = useRoute();
const peripheral = ref<Peripheral>();
const provenance = ref<ProvenanceItem[]>([]);
const release = computed(() => String(route.params.release));
async function load() {
  const revision = typeof route.query.revision === "string" ? route.query.revision : undefined;
  peripheral.value = await getPeripheral(release.value, String(route.params.entity), revision);
  provenance.value = await getProvenance(
    release.value,
    peripheral.value.entity_id,
    peripheral.value.revision_id,
  );
}
onMounted(() => void load());
watch(
  () => route.fullPath,
  () => void load(),
);
</script>
<template>
  <div v-if="peripheral" class="page-shell">
    <nav class="breadcrumbs">
      <RouterLink to="/">Catalog</RouterLink><span>/</span><span>Peripheral IP</span><span>/</span
      ><strong>{{ peripheral.name }}</strong>
    </nav>
    <EntityHeader
      eyebrow="Independent IP revision"
      :title="peripheral.name"
      :description="peripheral.description"
      :revision="peripheral.revision_id"
    />
    <div class="fact-strip">
      <div>
        <span>Vendor revision</span><strong>{{ peripheral.vendor_revision }}</strong>
      </div>
      <div>
        <span>Registers</span
        ><strong>{{ peripheral.register_count ?? peripheral.registers.length }}</strong>
      </div>
      <div>
        <span>Instances</span><strong>{{ peripheral.used_by.length }}</strong>
      </div>
      <div><span>Compatibility</span><strong>Codegen compatible</strong></div>
    </div>
    <section class="section-block">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Layout</p>
          <h2>Register map</h2>
        </div>
        <span class="count">Offset order</span>
      </div>
      <div class="register-table">
        <p v-if="peripheral.registers_truncated" class="empty">
          Showing the first 1,000 registers. Use catalog search to find registers in large arrays.
        </p>
        <div class="table-head">
          <span>Offset</span><span>Name</span><span>Access</span><span>Reset</span
          ><span>Description</span>
        </div>
        <RouterLink
          v-for="register in peripheral.registers"
          :key="`${register.entity_id}${register.revision_id}`"
          :to="{
            path: `/releases/${release}/registers/${register.entity_id}`,
            query: { revision: register.revision_id },
          }"
          class="register-row"
          ><code>{{ register.offset }}</code
          ><strong>{{ register.name }}</strong
          ><span
            >{{ register.access.readable ? "R" : ""
            }}{{ register.access.writable ? "W" : "" }}</span
          ><code>{{ register.reset_value ?? "—" }}</code
          ><span>{{ register.description }}</span></RouterLink
        >
      </div>
    </section>
    <ProvenanceList :items="provenance" />
  </div>
  <p v-else class="page-loading">Loading peripheral revision…</p>
</template>
